import { ReactNode, useEffect, useRef } from 'react';

type ModalProps = {
    isOpen: boolean;
    title: string;
    description?: string;
    onClose: () => void;
    children: ReactNode;
};

export default function Modal({ isOpen, title, description, onClose, children }: ModalProps) {
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onCloseRef.current();
                return;
            }

            if (event.key !== 'Tab' || !dialogRef.current) return;

            const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
            );

            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const activeElement = document.activeElement;

            if (event.shiftKey && activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            previousFocusRef.current?.focus();
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const nextFocus = dialogRef.current?.querySelector<HTMLElement>(
            'input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])',
        );
        nextFocus?.focus();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto p-4">
            <button type="button" aria-label="Close dialog" className="absolute inset-0 bg-black/45" onClick={onClose} />
            <div className="relative z-10 flex min-h-full items-start justify-center py-2 sm:items-center">
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="modal-title"
                    aria-describedby={description ? 'modal-description' : undefined}
                    tabIndex={-1}
                    className="surface-card w-full max-w-3xl max-h-[calc(100vh-1.5rem)] overflow-y-auto rounded-3xl p-5 sm:p-6"
                >
                    <div className="mb-4 flex items-start justify-between gap-4">
                        <div>
                            <h2 id="modal-title" className="text-lg font-semibold">
                                {title}
                            </h2>
                            {description ? (
                                <p id="modal-description" className="mt-1 text-sm" style={{ color: 'var(--foreground-muted)' }}>
                                    {description}
                                </p>
                            ) : null}
                        </div>
                        <button type="button" onClick={onClose} className="button-secondary rounded-2xl px-3 py-2 text-sm font-medium">
                            Close
                        </button>
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
}
